import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { MutationFetchFunction } from 'common/helpers/common';
import { ApiError, ApiResult } from 'common/repositories/common.model';

import {
  ActivitySaleModel,
  ActivitySaleMutationInput,
  ActivitySaleDeleteInput,
  ActivitySaleUpdateInput,
  ActivitySaleReschedulePutInput,
} from './model';

export function useCreateActivitySale(
  options?: UseMutationOptions<
    ApiResult<ActivitySaleModel>,
    ApiError,
    ActivitySaleMutationInput
  >,
): UseMutationResult<
  ApiResult<ActivitySaleModel>,
  ApiError,
  ActivitySaleMutationInput
> {
  return useMutation<
    ApiResult<ActivitySaleModel>,
    ApiError,
    ActivitySaleMutationInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: 'activity-sales',
      method: 'POST',
      classType: ActivitySaleModel,
      body,
    });
  }, options);
}

export function useUpdateActivitySale(
  options?: UseMutationOptions<
    ApiResult<ActivitySaleModel>,
    ApiError,
    ActivitySaleUpdateInput
  >,
): UseMutationResult<
  ApiResult<ActivitySaleModel>,
  ApiError,
  ActivitySaleUpdateInput
> {
  return useMutation<
    ApiResult<ActivitySaleModel>,
    ApiError,
    ActivitySaleUpdateInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: `activity-sales/${body.activitySaleId}`,
      method: 'PUT',
      classType: ActivitySaleModel,
      body: body.body,
    });
  }, options);
}

export function useRescheduleActivitySale(
  options?: UseMutationOptions<
    ApiResult<ActivitySaleModel>,
    ApiError,
    ActivitySaleReschedulePutInput
  >,
): UseMutationResult<
  ApiResult<ActivitySaleModel>,
  ApiError,
  ActivitySaleReschedulePutInput
> {
  return useMutation<
    ApiResult<ActivitySaleModel>,
    ApiError,
    ActivitySaleReschedulePutInput
  >(async function (body) {
    return await MutationFetchFunction({
      url: `activity-sales/${body.activitySaleId}/reschedule`,
      method: 'PUT',
      body: body.body,
    });
  }, options);
}

export function useDeleteActivitySale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    ActivitySaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, ActivitySaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, ActivitySaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `activity-sales/${body.id}`,
        method: 'DELETE',
      });
    },
    options,
  );
}

export function useVoidActivitySale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    ActivitySaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, ActivitySaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, ActivitySaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `activity-sales/${body.id}/void`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useCompleteActivitySale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    ActivitySaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, ActivitySaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, ActivitySaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `activity-sales/${body.id}/complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}

export function useRevertCompleteActivitySale(
  options?: UseMutationOptions<
    { message: string },
    ApiError,
    ActivitySaleDeleteInput
  >,
): UseMutationResult<{ message: string }, ApiError, ActivitySaleDeleteInput> {
  return useMutation<{ message: string }, ApiError, ActivitySaleDeleteInput>(
    async function (body) {
      return await MutationFetchFunction({
        url: `activity-sales/${body.id}/revert-complete`,
        method: 'PATCH',
      });
    },
    options,
  );
}
